import React, { Component } from 'react';
import { PlantDto, VoidFunc } from '@trucktrax/trucktrax-ts-common';
import { Button, postGISToGoogleMapsPoints, vars } from '@trucktrax/trucktrax-common';
import cx from 'classnames';
import { isEqual } from 'lodash';
import messagingConstants from '../../../../constants/messagingConstants';
import { NO_ADDITIONAL_FUNCTIONS_AVAILABLE } from '../../../../constants/appConstants';
import icons from '../icons';
import TTMarker from './TTMarker';
import styles from '../TTMap.module.css';
import MarkerLabel from './MarkerLabel';
import { isValidLatLng } from '../../../../util/geoUtil';

export default class PlantMarkerWithInfoPane extends Component<
  PlantMarkerWithInfoPaneProps,
  PlantMarkerWithInfoPaneState
> {
  static defaultProps = {
    showGeofence: false,
    markerColor: vars.gray700,
    mapRef: undefined,
    isMessagingEnabled: true,
  };

  constructor(props: PlantMarkerWithInfoPaneProps) {
    super(props);

    this.state = {
      avoidRerender: false,
    };
  }

  markerRef: any;

  geozones: (google.maps.Circle | google.maps.Polygon)[] = [];

  renderGeoZones = () => {
    this.removePlantGeozonesFromMap();

    const { plant, showGeofence, markerColor } = this.props;
    if (showGeofence && plant.geozones && plant.geozones.length > 0) {
      const shapeStyles = {
        fillColor: markerColor,
        fillOpacity: 0.25,
        strokeColor: markerColor,
        strokeWeight: 2,
        strokeOpacity: 0.8,
        clickable: false,
        zIndex: 1,
      };

      for (let i = 0; i < plant.geozones.length; i += 1) {
        const geozone = plant.geozones[i].zone;
        if (geozone?.circle) {
          const position = {
            lat: geozone?.circle?.center?.latitude ?? 0,
            lng: geozone?.circle?.center?.longitude ?? 0,
          };

          if (position && isValidLatLng(position.lat, position.lng)) {
            const circleGeozone = new google.maps.Circle({
              center: position,
              radius: geozone?.circle?.radius,
              map: this.props.mapRef,
              ...shapeStyles,
            });
            this.geozones.push(circleGeozone);
          }
        } else if (geozone?.polygon) {
          const polygonGeozone = new google.maps.Polygon({
            paths: postGISToGoogleMapsPoints(
              geozone!.polygon!.points!,
            ) as google.maps.LatLngLiteral[],
            map: this.props.mapRef,
            ...shapeStyles,
          });
          this.geozones.push(polygonGeozone);
        }
      }
    }

    if (!showGeofence) {
      this.removePlantGeozonesFromMap();
    }

    return <></>;
  };

  componentWillUnmount(): void {
    this.removePlantGeozonesFromMap();
  }

  componentDidUpdate(prevProps: Readonly<PlantMarkerWithInfoPaneProps>): void {
    const geozonesChanged = !isEqual(prevProps.plant.geozones, this.props.plant.geozones);
    if (geozonesChanged) {
      this.removePlantGeozonesFromMap();
      this.setState({ avoidRerender: false });
    } else if (!this.state.avoidRerender) {
      this.setState({ avoidRerender: true });
    }
    const showGeofencesChanged = prevProps.showGeofence !== this.props.showGeofence;
    if (showGeofencesChanged) {
      this.setState({ avoidRerender: false });
    }
  }

  removePlantGeozonesFromMap = (): void => {
    this.geozones.forEach(geozone => {
      geozone.setMap(null);
    });
  };

  handleMessageAllDriversForPlant = () =>
    this.props.updateToFieldValueWithPlantDrivers(this.props.plant.url || '');

  generatePlantInfoContent = () => {
    // On the Incoming Materials map messaging is disabled (isMessagingEnabled=false):
    // the destination-plant link is geofence-based and most drivers are external, so
    // "Message All Plant's Drivers" doesn't apply. Show a fallback instead. (DEV-3891)
    if (!this.props.isMessagingEnabled) {
      return (
        <div className={styles.plantPaneBtnContainer}>
          <div data-test="plant-infopane-no-functions">{NO_ADDITIONAL_FUNCTIONS_AVAILABLE}</div>
        </div>
      );
    }

    const sendMessageButton = (
      <div className={styles.buttonHover}>
        <Button
          buttonClassName={cx('tt-btn-basic', styles.plantSendMessageBtn)}
          onClick={this.handleMessageAllDriversForPlant}
          iconClassName="icon-messages"
          dataTest="plant-infopane-new-message-button"
          name={messagingConstants.MESSAGE_ALL_PLANTS_DRIVERS}
        />
      </div>
    );

    const buttons = <div className={styles.plantPaneBtns}>{sendMessageButton}</div>;

    return (
      <div className={styles.plantPaneBtnContainer}>
        <div>{buttons}</div>
      </div>
    );
  };

  render() {
    const { plant, markerColor, mapRef, isSelected, ...rest } = this.props;
    if (mapRef && this.markerRef) {
      this.markerRef.setMap(mapRef);
    }
    try {
      const position = {
        lat: plant.location!.latitude ?? 0,
        lng: plant.location!.longitude ?? 0,
      };

      const plantIconAnchorX = 11;
      const plantIconAnchorY = 17;

      const icon = {
        path: icons.housePath,
        fillColor: markerColor,
        fillOpacity: 1,
        strokeWeight: 0,
        // Updated anchor point syntax for compatibility
        anchor: { x: plantIconAnchorX, y: plantIconAnchorY },
      };

      const shadowIcon = {
        ...icon,
        fillColor: 'black',
        fillOpacity: 0.5,
        scale: 1.075,
        strokeWeight: 2,
        strokeColor: 'black',
        strokeOpacity: 0.15,
        anchor: { x: plantIconAnchorX + 0.5, y: plantIconAnchorY - 1 },
      };

      const infoPaneTitle = (
        <div data-test="plant-title" title={plant.name}>
          <span className={cx(styles.titleText, 'nowrap-ellipsis')}>{plant.name}</span>
        </div>
      );
      const label = (plant.name && (
        <MarkerLabel
          color={markerColor}
          name={plant.name}
          containerStyles={{ transform: 'translate(-50%, 10px)' }}
        />
      )) as JSX.Element | undefined;

      // pixelOffset improves centering of infoPane around marker
      const pixelOffset: google.maps.Size = {
        width: 1.5,
        height: 7,
        equals: other => other?.width === 1.5 && other.height === 7,
      };

      return (
        <div key={plant.url}>
          <TTMarker
            position={position}
            icon={icon}
            infoPaneTitle={infoPaneTitle}
            infoPaneContent={this.generatePlantInfoContent()}
            shadowIcon={shadowIcon}
            label={label}
            pixelOffset={pixelOffset}
            isSelected={isSelected}
            mapRef={mapRef}
            {...rest}
            isLabelHover
            isPlantMarker
            canHover
          />
          {!this.state.avoidRerender && this.renderGeoZones()}
        </div>
      );
    } catch (e) {
      // this avoids rendering component on errors
      // to debug, log this exception
      return null;
    }
  }
}

export interface PlantMarkerWithInfoPaneProps {
  plant: PlantDto;
  showGeofence?: boolean;
  markerColor: string;
  mapRef?: any;
  updateToFieldValueWithPlantDrivers: (url: string) => void;
  isMessagingEnabled?: boolean;
  onClick?: VoidFunc;
  onCloseClick?: VoidFunc;
  isSelected?: boolean;
  zIndex?: number;
}

interface PlantMarkerWithInfoPaneState {
  avoidRerender: boolean;
}
